/**
 * 日時関連のユーティリティ関数
 */

import type { components } from '@/types/api-schema';
import { DATETIME_TYPE } from '@/constants/datetimeTypes';
import {
  EVENT_TEMPORAL_STATE,
  type EventTemporalState,
} from '@/constants/eventTemporalStates';
import { PERIOD_TYPE } from '@/constants/periodTypes';
import { EVENT_STATUS, type EventStatusType } from '@/constants/eventStatuses';
import * as JapaneseHolidays from 'japanese-holidays';

type DateTimeInfo = components['schemas']['DateTimeInfo'];
type DateTimePeriod = components['schemas']['DateTimePeriod'];

/**
 * 終了日時が未設定の datetime/datetime_range で、日付が変わる直前に開始する
 * イベントが即座に past 扱いになるのを防ぐためのバッファ。
 * 例: 23:30 開始のイベントは翌日 05:30 まで「開催中」とみなす。
 */
const LATE_NIGHT_BUFFER_MS = 6 * 60 * 60 * 1000;

/**
 * 指定時刻における fixed イベントの時系列状態。
 *
 * - start が未来: future
 * - start <= now <= end: ongoing（end が未設定の場合は periodType の粒度に応じた暫定終了時刻を採用）
 *   - year_month: 翌月1日0:00
 *   - datetime / datetime_range: max(翌日0:00, 開始+LATE_NIGHT_BUFFER_MS)
 *   - date / date_range / その他: 翌日0:00
 * - それ以外: past
 * - start が欠落: undecided
 */
function getFixedTemporalState(
  period: DateTimePeriod | null | undefined,
  referenceTime: number
): EventTemporalState {
  const startStr = period?.startDatetime;
  if (!startStr) return EVENT_TEMPORAL_STATE.UNDECIDED;
  const start = new Date(startStr).getTime();
  if (start > referenceTime) return EVENT_TEMPORAL_STATE.FUTURE;

  const endStr = period?.endDatetime;
  if (endStr) {
    const end = new Date(endStr).getTime();
    return referenceTime <= end
      ? EVENT_TEMPORAL_STATE.ONGOING
      : EVENT_TEMPORAL_STATE.PAST;
  }

  const startDate = new Date(start);
  const nextDayStart = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate() + 1
  ).getTime();
  let effectiveEnd: number;
  if (period?.periodType === PERIOD_TYPE.YEAR_MONTH) {
    effectiveEnd = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      1
    ).getTime();
  } else if (
    period?.periodType === PERIOD_TYPE.DATETIME ||
    period?.periodType === PERIOD_TYPE.DATETIME_RANGE
  ) {
    effectiveEnd = Math.max(nextDayStart, start + LATE_NIGHT_BUFFER_MS);
  } else {
    effectiveEnd = nextDayStart;
  }
  return referenceTime < effectiveEnd
    ? EVENT_TEMPORAL_STATE.ONGOING
    : EVENT_TEMPORAL_STATE.PAST;
}

/**
 * 指定時刻における poll イベントの時系列状態。ongoing には絶対にならない。
 *
 * - いずれかの候補日が未来（start > now）: future
 * - すべての候補日が過去（start <= now）: past
 * - 有効な候補日が無い: undecided
 */
function getPollTemporalState(
  periods: DateTimePeriod[] | null | undefined,
  referenceTime: number
): EventTemporalState {
  const starts =
    periods
      ?.map((p) => p.startDatetime)
      .filter((s): s is string => !!s && s.trim() !== '')
      .map((s) => new Date(s).getTime())
      .filter((t) => !Number.isNaN(t)) ?? [];
  if (starts.length === 0) return EVENT_TEMPORAL_STATE.UNDECIDED;
  return starts.some((t) => t > referenceTime)
    ? EVENT_TEMPORAL_STATE.FUTURE
    : EVENT_TEMPORAL_STATE.PAST;
}

/**
 * 指定時刻におけるイベントの時系列状態を返す。
 *
 * DateTimeType（fixed/poll/undecided）と直交する概念として、現在時刻に対する
 * 相対的な状態（past/ongoing/future/undecided）を判定する。
 *
 * - 中止（cancelled）・終了（completed）ステータスのイベントは、日時に関わらず past として扱う
 * - fixed: {@link getFixedTemporalState} 参照
 * - poll: {@link getPollTemporalState} 参照（ongoing にはならない）
 * - undecided または必要な情報が欠落している場合は undecided
 */
export function getEventTemporalState(
  datetimeInfo: DateTimeInfo | null | undefined,
  referenceTime: number = Date.now(),
  options?: { eventStatus?: EventStatusType | null }
): EventTemporalState {
  if (
    options?.eventStatus === EVENT_STATUS.CANCELLED ||
    options?.eventStatus === EVENT_STATUS.COMPLETED
  ) {
    return EVENT_TEMPORAL_STATE.PAST;
  }

  if (!datetimeInfo) return EVENT_TEMPORAL_STATE.UNDECIDED;

  switch (datetimeInfo.datetimeType) {
    case DATETIME_TYPE.FIXED:
      return getFixedTemporalState(datetimeInfo.fixedPeriod, referenceTime);
    case DATETIME_TYPE.POLL:
      return getPollTemporalState(datetimeInfo.pollPeriods, referenceTime);
    case DATETIME_TYPE.UNDECIDED:
    default:
      return EVENT_TEMPORAL_STATE.UNDECIDED;
  }
}

/**
 * DateTimeInfoから表示用の文字列を生成
 */
export function formatDateTimeInfo(datetimeInfo: DateTimeInfo | null | undefined): string {
  if (!datetimeInfo) {
    return '未定';
  }

  switch (datetimeInfo.datetimeType) {
    case 'undecided':
      return '日時未定';

    case 'fixed':
      if (datetimeInfo.fixedPeriod) {
        return formatDateTimePeriod(datetimeInfo.fixedPeriod);
      }
      return '未定';

    case 'poll':
      if (datetimeInfo.pollPeriods && datetimeInfo.pollPeriods.length > 0) {
        return `候補日 ${datetimeInfo.pollPeriods.length}件から選択`;
      }
      return '候補日から選択';

    default:
      return '未定';
  }
}

// --- 表記（docs/decisions/date-time-notation.md） ---
// 表示する時刻は閲覧端末のタイムゾーンではなく JST で数える（同じ日か・翌日かの判定も JST）。
// OG 画像（backend/src/common/og_image/notation.py）と同じ文字列になるように揃えている。

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

/** 翌日のこの時刻までに終わるときは「26:00」のように開始日の時刻として書く（朝5時） */
const MAX_EXTENDED_HOUR = 29;

/** 暦の上の日付（月は1始まり） */
export interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

interface JstDateTime extends CalendarDate {
  hour: number;
  minute: number;
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function toJst(date: Date): JstDateTime {
  const j = new Date(date.getTime() + JST_OFFSET_MS);
  return {
    year: j.getUTCFullYear(),
    month: j.getUTCMonth() + 1,
    day: j.getUTCDate(),
    hour: j.getUTCHours(),
    minute: j.getUTCMinutes(),
  };
}

/** 日付の通し番号（2つの日付の差が日数になる） */
function dayIndex({ year, month, day }: CalendarDate): number {
  return Date.UTC(year, month - 1, day) / DAY_MS;
}

/** JST の暦の日付 */
export function toJstCalendarDate(date: Date): CalendarDate {
  const { year, month, day } = toJst(date);
  return { year, month, day };
}

/** JST での今年。「今年以外のときだけ年を付ける」の基準 */
export function currentJstYear(now: number = Date.now()): number {
  return toJst(new Date(now)).year;
}

/** 曜日。国民の祝日は「火・祝」、振替休日・国民の休日は「月・休」（祝日法の区別に合わせる） */
export function formatWeekdayLabel(d: CalendarDate): string {
  const base = WEEKDAYS[new Date(Date.UTC(d.year, d.month - 1, d.day)).getUTCDay()];
  const name = JapaneseHolidays.isHolidayAt(JapaneseHolidays.jDate(d.year, d.month - 1, d.day));
  if (!name) return base;
  const isRestDay = name === '振替休日' || name === '国民の休日';
  return `${base}・${isRestDay ? '休' : '祝'}`;
}

/** 24時間制。時は0埋めしない（9:00） */
function hm(t: JstDateTime): string {
  return `${t.hour}:${pad2(t.minute)}`;
}

/** 長い形 10月18日(日)。基準年と違う年なら 2027年1月9日(土) */
function mdLong(d: CalendarDate, refYear: number): string {
  const year = d.year !== refYear ? `${d.year}年` : '';
  return `${year}${d.month}月${d.day}日(${formatWeekdayLabel(d)})`;
}

/** 短い形 10/18(日)。基準年と違う年なら 2027/1/9(土) */
function mdShort(d: CalendarDate, refYear: number): string {
  const year = d.year !== refYear ? `${d.year}/` : '';
  return `${year}${d.month}/${d.day}(${formatWeekdayLabel(d)})`;
}

/**
 * 終了時刻を開始日基準で書く。同じ日なら 21:00、翌日の 29:00 までなら 24:00〜29:00。
 * それより後なら null（終了にも日付を書く必要がある）
 */
function endTime(start: JstDateTime, end: JstDateTime): string | null {
  const days = dayIndex(end) - dayIndex(start);
  if (days === 0) return hm(end);
  if (days === 1 && (end.hour + 24) * 60 + end.minute <= MAX_EXTENDED_HOUR * 60) {
    return `${end.hour + 24}:${pad2(end.minute)}`;
  }
  return null;
}

function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** 日付の長い形（10月18日(日)）。今年以外は年を付ける */
export function formatDateLong(date: Date, refYear: number = currentJstYear()): string {
  return mdLong(toJst(date), refYear);
}

/** 日付の短い形（10/18(日)）。今年以外は年を付ける */
export function formatDateShort(date: Date, refYear: number = currentJstYear()): string {
  return mdShort(toJst(date), refYear);
}

/** ある時点の長い形（10月18日(日) 9:00）。回答期限や履歴の日時など、期間でないもの */
export function formatDateTimeLong(date: Date, refYear: number = currentJstYear()): string {
  const t = toJst(date);
  return `${mdLong(t, refYear)} ${hm(t)}`;
}

/** ある時点の短い形（10/18(日) 9:00）。コメントの投稿日時など狭い場所 */
export function formatDateTimeShort(date: Date, refYear: number = currentJstYear()): string {
  const t = toJst(date);
  return `${mdShort(t, refYear)} ${hm(t)}`;
}

/**
 * 確定日時の長い形（docs/decisions/date-time-notation.md）
 *
 * - year_month: 2026年11月（今年でも年を付ける）
 * - date: 10月18日(日)
 * - date_range: 10月17日(土)〜19日(月) / 10月31日(土)〜11月2日(月)
 * - datetime: 10月18日(日) 19:00〜
 * - datetime_range: 10月18日(日) 18:30〜21:00 / 22:00〜26:00 / 10月17日(土) 10:00〜10/18(日) 15:00
 *
 * @param refYear 「今年」の基準。省略時は JST の今年
 */
export function formatDateTimePeriod(
  period: DateTimePeriod,
  refYear: number = currentJstYear()
): string {
  const { periodType, startDatetime, endDatetime } = period;
  const startDate = parseDate(startDatetime);
  if (!startDate) {
    return '未定';
  }
  const s = toJst(startDate);
  const endDate = parseDate(endDatetime);
  const e = endDate ? toJst(endDate) : null;

  switch (periodType) {
    case PERIOD_TYPE.YEAR_MONTH:
      // 月だけでは何を指すか伝わりにくいので、今年でも年を付ける
      return `${s.year}年${s.month}月`;

    case PERIOD_TYPE.DATE_RANGE: {
      if (!e) return mdLong(s, refYear);
      const tail =
        e.year === s.year && e.month === s.month
          ? `${e.day}日(${formatWeekdayLabel(e)})`
          : mdLong(e, s.year); // 開始と同じ年なら省く（2027年1月30日(土)〜2月1日(月)）
      return `${mdLong(s, refYear)}〜${tail}`;
    }

    case PERIOD_TYPE.DATETIME:
      return `${mdLong(s, refYear)} ${hm(s)}〜`;

    case PERIOD_TYPE.DATETIME_RANGE: {
      if (!e) return `${mdLong(s, refYear)} ${hm(s)}〜`;
      const end = endTime(s, e) ?? `${mdShort(e, s.year)} ${hm(e)}`;
      return `${mdLong(s, refYear)} ${hm(s)}〜${end}`;
    }

    case PERIOD_TYPE.DATE:
    default:
      return mdLong(s, refYear);
  }
}

/**
 * 候補日一覧を表示用に整形
 */
export function formatPollPeriods(periods: DateTimePeriod[] | null | undefined): string[] {
  if (!periods || periods.length === 0) {
    return [];
  }

  return periods.map((period) => formatDateTimePeriod(period));
}

/**
 * 候補日の短い形（候補日の投票画面など、狭い場所に並べるとき）
 *
 * - year_month: 2026年11月（今年でも年を付ける）
 * - date: 10/18(日・祝)。今年以外は 2027/1/9(土)
 * - date_range: 10/17(土)〜19(月) / 10/31(土)〜11/1(日)
 * - datetime: 10/18(日) 19:00〜
 * - datetime_range: 10/18(日) 9:00〜12:00 / 22:00〜26:00 / 10/17(土) 10:00〜10/18(日) 15:00
 *
 * @param refYear 「今年」の基準。省略時は JST の今年
 */
export function formatPollPeriodCompact(
  period: DateTimePeriod,
  refYear: number = currentJstYear()
): string {
  const { periodType, startDatetime, endDatetime } = period;
  const startDate = parseDate(startDatetime);
  if (!startDate) {
    return '未定';
  }
  const s = toJst(startDate);
  const endDate = parseDate(endDatetime);
  const e = endDate ? toJst(endDate) : null;

  switch (periodType) {
    case PERIOD_TYPE.YEAR_MONTH:
      return `${s.year}年${s.month}月`;

    case PERIOD_TYPE.DATE_RANGE: {
      if (!e) return mdShort(s, refYear);
      const tail =
        e.year === s.year && e.month === s.month
          ? `${e.day}(${formatWeekdayLabel(e)})`
          : mdShort(e, s.year);
      return `${mdShort(s, refYear)}〜${tail}`;
    }

    case PERIOD_TYPE.DATETIME:
      return `${mdShort(s, refYear)} ${hm(s)}〜`;

    case PERIOD_TYPE.DATETIME_RANGE: {
      if (!e) return `${mdShort(s, refYear)} ${hm(s)}〜`;
      const end = endTime(s, e) ?? `${mdShort(e, s.year)} ${hm(e)}`;
      return `${mdShort(s, refYear)} ${hm(s)}〜${end}`;
    }

    case PERIOD_TYPE.DATE:
    default:
      return mdShort(s, refYear);
  }
}

/**
 * ISO日時を「たった今 / n分前 / n時間前 / n日前 / 日付」の相対表記にする。
 * 通知一覧など、直近の出来事を一覧表示する場面で使う。
 */
export function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);
  if (diffMinutes < 1) return 'たった今';
  if (diffMinutes < 60) return `${diffMinutes}分前`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}時間前`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}日前`;
  return formatDateLong(date);
}

/**
 * 回答締切(responseDeadline)を過ぎているかを判定する。
 * 未設定・パース不能な値は締切なし(false)として扱う
 */
export function isResponseDeadlinePassed(
  responseDeadline: string | null | undefined,
  referenceTime: number = Date.now()
): boolean {
  if (!responseDeadline) return false;
  const deadline = new Date(responseDeadline).getTime();
  if (Number.isNaN(deadline)) return false;
  return referenceTime > deadline;
}

/**
 * 回答締切の表示ラベル（例: 8月15日(金) 23:59）。今年でなければ年を含める
 */
export function formatResponseDeadline(iso: string): string {
  const date = parseDate(iso);
  return date ? formatDateTimeLong(date) : '';
}

/**
 * 締切までの残り時間ラベル（例: あと3日 / あと5時間 / あと10分）。
 * 締切済み・パース不能は null
 */
export function formatTimeUntilDeadline(
  iso: string,
  referenceTime: number = Date.now()
): string | null {
  const deadline = new Date(iso).getTime();
  if (Number.isNaN(deadline)) return null;
  const diff = deadline - referenceTime;
  if (diff <= 0) return null;
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `あと${days}日`;
  if (hours >= 1) return `あと${hours}時間`;
  return `あと${Math.max(minutes, 1)}分`;
}

/**
 * ISO 8601 → datetime-local input 値（ローカル時刻 YYYY-MM-DDTHH:MM）。不正・空は ''
 */
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * datetime-local input 値（ローカル時刻）→ ISO 8601 (UTC)。空・不正は null
 */
export function datetimeLocalToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}
